"use client";

import { useCallback, useRef } from "react";

import { trackIngredient } from "@/lib/analytics";
import { isBuiltInCategory } from "@/lib/categories";
import { dedupeIngredientsWithActions } from "@/lib/categorization/dedupe";
import { scaleQuantityWithMemory } from "@/lib/categorization/quantity-scaler";
import {
  categorizeIngredient,
  parseQuantity,
  processIngredientText,
} from "@/lib/categorization/utils";
import { createTimestamp } from "@/lib/sync/operations";
import type { AppState, Category, Ingredient, IngredientList, Operation } from "@/lib/types";
import { generateId } from "@/lib/utils";

interface UseIngredientsProps {
  activeList: IngredientList | undefined;
  setState: React.Dispatch<React.SetStateAction<AppState>>;
  queueOperation: (operation: Operation) => void;
  replaceOrQueueOperation?: (operation: Operation) => void;
}

export function useIngredients({
  activeList,
  setState,
  queueOperation,
  replaceOrQueueOperation,
}: UseIngredientsProps) {
  const activeListRef = useRef(activeList);
  activeListRef.current = activeList;

  // Debouncing for quantity scaling operations
  // Tracks pending updates per ingredient to batch analytics and sync operations
  const quantityScaleTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const pendingQuantityUpdates = useRef<
    Map<
      string,
      {
        text: string;
        quantity: string | undefined;
        category: Category;
        hasQuantity: boolean;
        operation: Operation; // Store the operation to be queued
      }
    >
  >(new Map());

  const addIngredients = (inputValues: string[]) => {
    if (!activeList || inputValues.length === 0) return 0;

    const validValues = inputValues.filter((value) => value.trim());
    if (validValues.length === 0) return 0;

    // Get existing non-completed ingredients for dedupe matching
    const existingIngredients = activeList.ingredients
      .filter((ing) => !ing.completed)
      .map((ing) => ({
        id: ing.id,
        text: ing.text,
        quantity: ing.quantity,
      }));

    // Dedupe and get structured actions
    const { updates, additions } = dedupeIngredientsWithActions(
      validValues.map((v) => v.trim()),
      existingIngredients
    );

    // Build updates to existing with category info
    const updatesToExisting = updates.map((update) => {
      const existingIng = activeList.ingredients.find((ing) => ing.id === update.id);
      return {
        id: update.id,
        newQuantity: update.newQuantity,
        category: existingIng?.category || ("other" as Category),
      };
    });

    // Process additions
    const newIngredientsToAdd: Array<{
      text: string;
      quantity: string | undefined;
      category: Category;
    }> = additions.map((additionText) => {
      const quantity = parseQuantity(additionText);
      const category = categorizeIngredient(additionText);
      const processedText = processIngredientText(additionText, quantity);
      return { text: processedText, quantity, category };
    });

    // Create new ingredient objects
    const newIngredients: Ingredient[] = newIngredientsToAdd.map((item) => ({
      id: generateId(),
      text: item.text,
      category: item.category,
      quantity: item.quantity,
      completed: false,
    }));

    // Update local state - both updates and additions
    setState((prev) => ({
      ...prev,
      lists: prev.lists.map((list) => {
        if (list.id !== activeList.id) return list;

        // First, apply updates to existing ingredients
        let updatedIngredients = list.ingredients.map((ing) => {
          const update = updatesToExisting.find((u) => u.id === ing.id);
          if (update) {
            return {
              ...ing,
              quantity: update.newQuantity,
            };
          }
          return ing;
        });

        // Then, add new ingredients
        updatedIngredients = [...updatedIngredients, ...newIngredients];

        return { ...list, ingredients: updatedIngredients };
      }),
    }));

    // Queue operations
    const timestamp = createTimestamp();

    // Queue update operations for merged ingredients
    updatesToExisting.forEach((update) => {
      queueOperation({
        type: "UPDATE_INGREDIENT",
        id: update.id,
        changes: {
          quantity: update.newQuantity,
        },
        timestamp,
      });
    });

    // Queue add operations for new ingredients
    newIngredients.forEach((ingredient) => {
      queueOperation({
        type: "ADD_INGREDIENT",
        listId: activeList.id,
        ingredient,
        timestamp,
      });
    });

    // Track analytics
    const totalProcessed = updatesToExisting.length + newIngredients.length;
    if (totalProcessed > 0) {
      if (updatesToExisting.length > 0) {
        // Merged ingredients are tracked as edited
        trackIngredient("edited", {
          count: updatesToExisting.length,
          hasQuantity: true,
        });
      }
      if (newIngredients.length > 0) {
        trackIngredient("added", {
          count: newIngredients.length,
          hasQuantity: newIngredients.some((ing) => !!ing.quantity),
        });
      }
    }

    return totalProcessed; // Return count of successfully processed ingredients
  };

  const toggleIngredient = useCallback(
    (ingredientId: string) => {
      const list = activeListRef.current;
      if (!list) return;

      const ingredient = list.ingredients.find((ing) => ing.id === ingredientId);
      if (!ingredient) return;

      const newCompleted = !ingredient.completed;
      const newCompletedAt = newCompleted ? new Date() : undefined;

      setState((prev) => ({
        ...prev,
        lists: prev.lists.map((l) =>
          l.id === list.id
            ? {
                ...l,
                ingredients: l.ingredients.map((ing) =>
                  ing.id === ingredientId
                    ? {
                        ...ing,
                        completed: newCompleted,
                        completedAt: newCompletedAt,
                      }
                    : ing
                ),
              }
            : l
        ),
      }));

      queueOperation({
        type: "UPDATE_INGREDIENT",
        id: ingredientId,
        changes: {
          completed: newCompleted,
          completedAt: newCompletedAt,
        },
        timestamp: createTimestamp(),
      });

      trackIngredient(newCompleted ? "archived" : "restored", {
        category: ingredient.category,
      });
    },
    [setState, queueOperation]
  );

  const deleteIngredient = useCallback(
    (ingredientId: string) => {
      const list = activeListRef.current;
      if (!list) return;

      const ingredient = list.ingredients.find((ing) => ing.id === ingredientId);

      setState((prev) => ({
        ...prev,
        lists: prev.lists.map((l) =>
          l.id === list.id
            ? {
                ...l,
                ingredients: l.ingredients.filter((ing) => ing.id !== ingredientId),
              }
            : l
        ),
      }));

      queueOperation({
        type: "DELETE_INGREDIENT",
        id: ingredientId,
        timestamp: createTimestamp(),
      });

      if (ingredient) {
        trackIngredient("deleted", {
          category: ingredient.category,
        });
      }
    },
    [setState, queueOperation]
  );

  const updateIngredient = useCallback(
    (ingredientId: string, newText: string) => {
      const list = activeListRef.current;
      if (!list || !newText.trim()) return false;

      const rawText = newText.trim();
      const quantity = parseQuantity(rawText);
      // A category the app doesn't make (an agent's own) is kept; a built-in one follows the new text.
      const current = list.ingredients.find((ing) => ing.id === ingredientId)?.category;
      const category =
        current && !isBuiltInCategory(current) ? current : categorizeIngredient(rawText);
      const processedText = processIngredientText(rawText, quantity);

      setState((prev) => ({
        ...prev,
        lists: prev.lists.map((l) =>
          l.id === list.id
            ? {
                ...l,
                ingredients: l.ingredients.map((ing) =>
                  ing.id === ingredientId
                    ? {
                        ...ing,
                        text: processedText,
                        quantity,
                        category,
                      }
                    : ing
                ),
              }
            : l
        ),
      }));

      queueOperation({
        type: "UPDATE_INGREDIENT",
        id: ingredientId,
        changes: {
          text: processedText,
          quantity,
          category,
        },
        timestamp: createTimestamp(),
      });

      trackIngredient("edited", {
        category,
        hasQuantity: !!quantity,
      });

      return true;
    },
    [setState, queueOperation]
  );

  const archiveAllIngredients = () => {
    if (!activeList) return;

    const activeIngredients = activeList.ingredients.filter((ing) => !ing.completed);
    if (activeIngredients.length === 0) return;

    const timestamp = createTimestamp();
    const completedAt = new Date();

    // Update local state
    setState((prev) => ({
      ...prev,
      lists: prev.lists.map((list) =>
        list.id === activeList.id
          ? {
              ...list,
              ingredients: list.ingredients.map((ing) =>
                !ing.completed
                  ? {
                      ...ing,
                      completed: true,
                      completedAt,
                    }
                  : ing
              ),
            }
          : list
      ),
    }));

    // Queue operations for each ingredient
    activeIngredients.forEach((ingredient) => {
      queueOperation({
        type: "UPDATE_INGREDIENT",
        id: ingredient.id,
        changes: {
          completed: true,
          completedAt,
        },
        timestamp,
      });
    });

    // Track analytics once with count
    if (activeIngredients.length > 0) {
      trackIngredient("archived", {
        count: activeIngredients.length,
      });
    }
  };

  const deleteAllIngredients = () => {
    if (!activeList) return;

    const activeIngredients = activeList.ingredients.filter((ing) => !ing.completed);
    if (activeIngredients.length === 0) return;

    const timestamp = createTimestamp();

    // Update local state - remove only non-completed ingredients
    setState((prev) => ({
      ...prev,
      lists: prev.lists.map((list) =>
        list.id === activeList.id
          ? {
              ...list,
              ingredients: list.ingredients.filter((ing) => ing.completed),
            }
          : list
      ),
    }));

    // Queue operations for each ingredient
    activeIngredients.forEach((ingredient) => {
      queueOperation({
        type: "DELETE_INGREDIENT",
        id: ingredient.id,
        timestamp,
      });
    });

    // Track analytics once with count
    if (activeIngredients.length > 0) {
      trackIngredient("deleted", {
        count: activeIngredients.length,
      });
    }
  };

  const scaleIngredientQuantity = useCallback(
    (ingredientId: string, direction: "increment" | "decrement") => {
      const list = activeListRef.current;
      if (!list) return;

      const ingredient = list.ingredients.find((ing) => ing.id === ingredientId);
      if (!ingredient) return;

      const fullText = ingredient.quantity
        ? `${ingredient.quantity} ${ingredient.text}`
        : ingredient.text;

      const previousQuantity = ingredient.quantityMemory || null;

      const { text: scaledText, currentQuantity } = scaleQuantityWithMemory(
        fullText,
        direction,
        previousQuantity
      );

      const quantity = parseQuantity(scaledText);
      const processedText = processIngredientText(scaledText, quantity);
      const category = ingredient.category;

      const quantityMemory =
        currentQuantity || (ingredient.quantity ? ingredient.quantity : undefined);

      setState((prev) => ({
        ...prev,
        lists: prev.lists.map((l) =>
          l.id === list.id
            ? {
                ...l,
                ingredients: l.ingredients.map((ing) =>
                  ing.id === ingredientId
                    ? {
                        ...ing,
                        text: processedText,
                        quantity,
                        category,
                        quantityMemory,
                      }
                    : ing
                ),
              }
            : l
        ),
      }));

      const operation: Operation = {
        type: "UPDATE_INGREDIENT",
        id: ingredientId,
        changes: {
          text: processedText,
          quantity,
          category,
        },
        timestamp: createTimestamp(),
      };

      pendingQuantityUpdates.current.set(ingredientId, {
        text: processedText,
        quantity,
        category,
        hasQuantity: !!quantity,
        operation,
      });

      const existingTimeout = quantityScaleTimeouts.current.get(ingredientId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      const timeout = setTimeout(() => {
        const update = pendingQuantityUpdates.current.get(ingredientId);
        if (!update) return;

        if (replaceOrQueueOperation) {
          replaceOrQueueOperation(update.operation);
        } else {
          queueOperation(update.operation);
        }

        trackIngredient("edited", {
          category: update.category,
          hasQuantity: update.hasQuantity,
        });

        pendingQuantityUpdates.current.delete(ingredientId);
        quantityScaleTimeouts.current.delete(ingredientId);
      }, 3000);

      quantityScaleTimeouts.current.set(ingredientId, timeout);
    },
    [setState, queueOperation, replaceOrQueueOperation]
  );

  const incrementQuantity = useCallback(
    (ingredientId: string) => {
      scaleIngredientQuantity(ingredientId, "increment");
    },
    [scaleIngredientQuantity]
  );

  const decrementQuantity = useCallback(
    (ingredientId: string) => {
      scaleIngredientQuantity(ingredientId, "decrement");
    },
    [scaleIngredientQuantity]
  );

  return {
    addIngredients,
    toggleIngredient,
    deleteIngredient,
    updateIngredient,
    archiveAllIngredients,
    deleteAllIngredients,
    incrementQuantity,
    decrementQuantity,
  };
}
